import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { InfoIcon } from "lucide-react";
import {
  investorProfileQueryOptions,
  useClient,
  useSendHolderApprovalLink,
} from "creso-core/hooks";
import {
  findPrimaryHolder,
  findSecondHolder,
  formatHolderMobile,
} from "creso-core/utils";
import { Button } from "web-design-system";
import { handleError } from "@/utils/error";
import {
  useRefetchClientStatus,
  useSecondHolderClientId,
} from "@crm/hooks/use-second-holder";
import { HolderCard } from "./holder-card";

export function HolderApprovalStatus({
  onClose,
  onEditSecondHolder,
}: {
  onClose: () => void;
  onEditSecondHolder: () => void;
}) {
  const clientId = useSecondHolderClientId();
  const { data: client } = useClient(clientId, clientId > 0);
  const { data: profiles } = useQuery(
    investorProfileQueryOptions(
      { client_id: clientId },
      { enabled: clientId > 0 }
    )
  );
  const primaryHolder = findPrimaryHolder(profiles);
  const secondHolder = findSecondHolder(profiles);
  const allApproved =
    !!primaryHolder?.is_approved && !!secondHolder?.is_approved;
  const refetchClientStatus = useRefetchClientStatus(clientId);

  const handleDone = async () => {
    onClose();
    await refetchClientStatus();
  };
  const { mutateAsync: sendHolderApprovalLink, isPending } =
    useSendHolderApprovalLink();

  const handleNotify = async () => {
    const accountId = client?.investment_account;
    if (!accountId) {
      handleError(
        new Error("Missing investment account"),
        "Failed to notify holders"
      );
      return;
    }

    const pendingProfileIds = [primaryHolder, secondHolder]
      .filter((holder) => holder && !holder.is_approved)
      .map((holder) => holder?.investor_profile_guid)
      .filter((guid): guid is string => !!guid);

    if (pendingProfileIds.length === 0) {
      toast.success("Holders notified successfully");
      onClose();
      return;
    }

    try {
      await Promise.all(
        pendingProfileIds.map((profileId) =>
          sendHolderApprovalLink({
            clientId,
            investment_account_guid: accountId,
            profile_id: profileId,
          })
        )
      );
      toast.success("Holders notified successfully");
      onClose();
    } catch (error) {
      handleError(error, "Failed to notify holders");
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 pt-3">
      <div className="flex flex-col gap-4">
        <HolderCard
          name={client?.name ?? "—"}
          role="Primary holder"
          pan={client?.pan || "—"}
          mobile={formatHolderMobile(undefined, client?.phone_number)}
          email={client?.email || "—"}
          isApproved={primaryHolder?.is_approved}
        />
        <HolderCard
          name={secondHolder?.name ?? "—"}
          role="Second holder"
          pan={secondHolder?.pan || "—"}
          mobile={formatHolderMobile(
            undefined,
            secondHolder?.phone_number ?? undefined
          )}
          email={secondHolder?.email || "—"}
          isApproved={secondHolder?.is_approved}
          onEdit={allApproved ? undefined : onEditSecondHolder}
        />
      </div>
      <div className="mt-auto flex flex-col gap-4">
        <div className="flex items-start gap-2 rounded-lg border border-border bg-tag-slate p-3 text-tag-slate-foreground">
          <InfoIcon className="size-4 shrink-0 stroke-tag-slate-foreground" />
          <span className="text-xs">
            <span className="font-semibold">Approval Required.</span> Approval
            is required from both holders to finish joint account setup.
          </span>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            disabled={isPending}
            onClick={allApproved ? handleDone : handleNotify}
          >
            {allApproved ? "Done" : "Notify"}
          </Button>
        </div>
      </div>
    </div>
  );
}
